import { faFileUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@mui/material';
import { Theme } from '@mui/material/styles';
import { createStyles, makeStyles } from '@mui/styles';
import React from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';

const MAX_FILE_SIZE_BYTES = 31457280;
const ACCEPTED_TYPES = {
  'application/pdf': ['.pdf'],
  'image/png': ['.png'],
  'image/jpeg': ['.jpeg', '.jpg'],
};

/**
 * Kept in the layout (instead of react-dropzone's `display: none`) so the native
 * picker is reachable in hardened browsers/extensions that drop programmatic
 * clicks on hidden file inputs. Visually hidden using the standard sr-only recipe.
 */
const HIDDEN_INPUT_STYLE: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  border: 0,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  opacity: 0,
};

const useStyles = makeStyles((theme: Theme) => createStyles({
  root: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    minHeight: theme.spacing(18),
    padding: theme.spacing(4, 2),
    border: `2px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    // Wrapped copy stays flush left: centred or justified lines leave uneven gaps ("rivers").
    textAlign: 'left',
    cursor: 'pointer',
    outline: 'none',
    '&:focus-visible': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
    },
  },
  active: {
    borderColor: theme.palette.secondary.main,
    backgroundColor: theme.palette.action.hover,
  },
  disabled: {
    cursor: 'default',
    opacity: theme.palette.action.disabledOpacity,
  },
  icon: {
    color: theme.palette.tab.offselected,
    fontSize: theme.typography.h5.fontSize,
  },
  title: {
    color: theme.palette.text.primary,
  },
  muted: {
    color: theme.palette.text.secondary,
  },
}));

type FileDropzoneProps = {
  disabled: boolean;
  onFiles: (files: File[]) => void;
  onRejected: (rejections: FileRejection[]) => void;
  onNoFile: () => void;
};

export const FileDropzone = ({
  disabled, onFiles, onRejected, onNoFile,
}: FileDropzoneProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: ACCEPTED_TYPES,
    maxSize: MAX_FILE_SIZE_BYTES,
    multiple: true,
    disabled,
    onDropAccepted: onFiles,
    onDropRejected: onRejected,
    onDrop: (accepted, rejected) => {
      if (accepted.length === 0 && rejected.length === 0) onNoFile();
    },
  });
  const className = [
    classes.root,
    isDragActive ? classes.active : '',
    disabled ? classes.disabled : '',
  ].filter(Boolean).join(' ');

  return (
    <div {...getRootProps({ className, role: 'button', 'aria-disabled': disabled })}>
      <input {...getInputProps({ style: HIDDEN_INPUT_STYLE, 'aria-hidden': true })} />
      <FontAwesomeIcon icon={faFileUpload} className={classes.icon} aria-hidden="true" />
      <Typography variant="subtitle2" component="p" className={classes.title}>
        {t('imports:dropzone.title')}
      </Typography>
      <Typography variant="body2" className={classes.muted}>
        {t('imports:dropzone.hint')}
      </Typography>
      <Typography variant="caption" className={classes.muted}>
        {t('imports:dropzone.accepted')}
      </Typography>
    </div>
  );
};
